import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { 
  BarChart3, DollarSign, Calendar, TrendingUp, 
  Loader2, Filter, Truck, Map
} from "lucide-react";

import Sidebar from "../Includes/Sidebar";
import TopBar from "../Includes/TopBar";
import BottomNav from "../Includes/BottomNav";

const API_URL = process.env.REACT_APP_API_URL || "http://127.0.0.1:8000";

export default function Analytics() {
  const navigate = useNavigate();
  const user = (() => { try { return JSON.parse(localStorage.getItem("user")); } catch { return null; } })();
  const token = localStorage.getItem("user_token");

  const [isLoading, setIsLoading] = useState(true);
  const [activeNav, setActiveNav] = useState("analytics");
  const [sidebarOpen, setSidebarOpen] = useState(false);
  
  // Analytics State
  const [timeframe, setTimeframe] = useState("7"); // 7, 14, 30, 180, custom
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [data, setData] = useState({
    total_spent: 0,
    total_loads: 0,
    chart_data: [],
    start_date: "",
    end_date: ""
  });

  // Auth guard
  useEffect(() => {
    if (!user || !token) navigate("/company-login", { replace: true });
  }, [user, token, navigate]);

  // Fetch Data
// Update this specific function in Analytics.jsx
  const fetchAnalytics = async () => {
    if (!user?.email || !token) return;
    
    setIsLoading(true);
    try {
      let url = `${API_URL}/api/analytics/company/?email=${user.email}&timeframe=${timeframe}`;
      if (timeframe === "custom" && startDate && endDate) {
        url += `&start_date=${startDate}&end_date=${endDate}`;
      }

      const res = await fetch(url, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // CHECK IF RESPONSE IS ACTUALLY JSON BEFORE PARSING
      const contentType = res.headers.get("content-type");
      if (contentType && contentType.includes("application/json")) {
        const result = await res.json();
        if (res.ok) {
          setData(result);
        } else {
          console.error("API returned an error:", result);
        }
      } else {
        console.error("Server returned a non-JSON response. Likely a 500 HTML error page.");
      }
    } catch (err) {
      console.error("Failed to fetch analytics:", err);
    } finally {
      setIsLoading(false);
    }
  };
  // Refetch when standard timeframes change
  useEffect(() => {
    if (timeframe !== "custom") {
      fetchAnalytics();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [timeframe]);

  const handleCustomApply = () => {
    if (startDate && endDate) fetchAnalytics();
  };

  const companyName = user?.company_name || user?.name || "Company";
  const initials = companyName.split(" ").slice(0, 2).map(w => w[0]).join("").toUpperCase() || "CO";

  // Chart Utilities
  const maxSpend = Math.max(...(data.chart_data?.map(d => d.spent) || [0]), 10); // Minimum scale of 10 to prevent flatlining

  return (
    <div className="flex h-screen bg-gray-50 overflow-hidden" style={{ fontFamily: "Inter,system-ui,sans-serif" }}>
      {sidebarOpen && (
        <div className="fixed inset-0 bg-black/50 z-40 lg:hidden" onClick={() => setSidebarOpen(false)} />
      )}

      <Sidebar
        activeNav={activeNav} setActiveNav={setActiveNav}
        sidebarOpen={sidebarOpen} setSidebarOpen={setSidebarOpen}
        initials={initials} companyName={companyName}
      />

      <div className="flex-1 flex flex-col overflow-hidden min-w-0">
        <TopBar
          title="Spending Analytics"
          setSidebarOpen={setSidebarOpen}
          initials={initials}
        />

        <main className="flex-1 overflow-y-auto">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 py-6 sm:py-8 space-y-6">
            
            {/* Header & Controls */}
            <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
              <div>
                <h1 className="text-xl sm:text-2xl font-bold text-gray-900">Financial Overview</h1>
                <p className="text-sm text-gray-500 mt-1">Track your load posting expenditures over time.</p>
              </div>

              {/* Timeframe Selector */}
              <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3">
                <div className="bg-white p-1 rounded-xl border border-gray-200 shadow-sm flex overflow-x-auto w-full sm:w-auto">
                  {[
                    { val: "7", label: "7 Days" },
                    { val: "14", label: "14 Days" },
                    { val: "30", label: "30 Days" },
                    { val: "180", label: "6 Months" },
                    { val: "custom", label: "Custom" }
                  ].map(t => (
                    <button
                      key={t.val}
                      onClick={() => setTimeframe(t.val)}
                      className={`flex-shrink-0 px-4 py-2 rounded-lg text-xs font-semibold transition-all ${
                        timeframe === t.val ? "text-white" : "text-gray-500 hover:text-gray-900"
                      }`}
                      style={timeframe === t.val ? { background: "#1a2744" } : {}}
                    >
                      {t.label}
                    </button>
                  ))}
                </div>

                {/* Custom Date Pickers */}
                {timeframe === "custom" && (
                  <div className="flex items-center gap-2 animate-in fade-in slide-in-from-right-4">
                    <input 
                      type="date" 
                      value={startDate}
                      onChange={(e) => setStartDate(e.target.value)}
                      className="text-xs border border-gray-300 rounded-lg px-2 py-2 focus:ring-2 focus:ring-blue-100 outline-none"
                    />
                    <span className="text-gray-400 text-xs">to</span>
                    <input 
                      type="date" 
                      value={endDate}
                      onChange={(e) => setEndDate(e.target.value)}
                      className="text-xs border border-gray-300 rounded-lg px-2 py-2 focus:ring-2 focus:ring-blue-100 outline-none"
                    />
                    <button 
                      onClick={handleCustomApply}
                      disabled={!startDate || !endDate}
                      className="px-3 py-2 bg-blue-600 text-white text-xs font-bold rounded-lg disabled:opacity-50"
                    >
                      Apply
                    </button>
                  </div>
                )}
              </div>
            </div>

            {isLoading ? (
              <div className="flex flex-col items-center justify-center py-20 gap-3">
                <Loader2 className="w-8 h-8 animate-spin text-blue-500" />
                <p className="text-sm text-gray-400 font-medium">Crunching your numbers…</p>
              </div>
            ) : (
              <>
                <p className="text-xs font-semibold text-gray-400 uppercase tracking-widest">
                  Showing data from {data.start_date} to {data.end_date}
                </p>

                {/* KPI Cards */}
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                  {/* Total Spent */}
                  <div className="bg-white rounded-2xl border border-gray-100 p-5 shadow-sm relative overflow-hidden">
                    <div className="absolute top-0 right-0 p-4 opacity-10">
                      <DollarSign className="w-16 h-16 text-green-600" />
                    </div>
                    <div className="w-10 h-10 rounded-xl bg-green-50 flex items-center justify-center mb-4 border border-green-100">
                      <DollarSign className="w-5 h-5 text-green-600" />
                    </div>
                    <p className="text-xs font-bold text-gray-400 uppercase tracking-wider">Total Spent</p>
                    <h2 className="text-3xl font-black text-gray-900 mt-1">${data.total_spent.toFixed(2)}</h2>
                  </div>

                  {/* Total Loads */}
                  <div className="bg-white rounded-2xl border border-gray-100 p-5 shadow-sm relative overflow-hidden">
                    <div className="absolute top-0 right-0 p-4 opacity-10">
                      <Truck className="w-16 h-16 text-blue-600" />
                    </div>
                    <div className="w-10 h-10 rounded-xl bg-blue-50 flex items-center justify-center mb-4 border border-blue-100">
                      <Truck className="w-5 h-5 text-blue-600" />
                    </div>
                    <p className="text-xs font-bold text-gray-400 uppercase tracking-wider">Loads Posted</p>
                    <h2 className="text-3xl font-black text-gray-900 mt-1">{data.total_loads}</h2>
                  </div>

                  {/* Avg Cost Per Load */}
                  <div className="bg-white rounded-2xl border border-gray-100 p-5 shadow-sm relative overflow-hidden">
                    <div className="absolute top-0 right-0 p-4 opacity-10">
                      <TrendingUp className="w-16 h-16 text-indigo-600" />
                    </div>
                    <div className="w-10 h-10 rounded-xl bg-indigo-50 flex items-center justify-center mb-4 border border-indigo-100">
                      <TrendingUp className="w-5 h-5 text-indigo-600" />
                    </div>
                    <p className="text-xs font-bold text-gray-400 uppercase tracking-wider">Avg Cost Per Load</p>
                    <h2 className="text-3xl font-black text-gray-900 mt-1">
                      ${data.total_loads > 0 ? (data.total_spent / data.total_loads).toFixed(2) : "0.00"}
                    </h2>
                  </div>
                </div>

                {/* CSS Bar Chart */}
                <div className="bg-white rounded-2xl border border-gray-100 p-6 shadow-sm">
                  <div className="flex items-center gap-2 mb-8">
                    <BarChart3 className="w-5 h-5 text-gray-400" />
                    <h3 className="text-base font-bold text-gray-900">Daily Spending Breakdown</h3>
                  </div>

                  {data.chart_data?.length === 0 ? (
                    <div className="flex flex-col items-center justify-center py-12 text-center">
                      <Calendar className="w-12 h-12 text-gray-200 mb-3" />
                      <p className="text-sm font-semibold text-gray-700">No spending data</p>
                      <p className="text-xs text-gray-400 mt-1">You didn't post any loads in this timeframe.</p>
                    </div>
                  ) : (
                    <div className="h-64 flex items-end gap-2 overflow-x-auto pb-6 pt-4">
                      {data.chart_data.map((day, idx) => {
                        // Calculate percentage of max to set bar height
                        const heightPercent = maxSpend > 0 ? (day.spent / maxSpend) * 100 : 0;
                        
                        return (
                          <div key={idx} className="flex-1 min-w-[40px] max-w-[80px] flex flex-col justify-end group relative h-full">
                            
                            {/* Hover Tooltip */}
                            <div className="opacity-0 group-hover:opacity-100 absolute -top-12 left-1/2 -translate-x-1/2 bg-gray-900 text-white text-[10px] py-1.5 px-3 rounded-lg pointer-events-none transition-opacity whitespace-nowrap z-10 shadow-lg">
                              <p className="font-bold">${day.spent.toFixed(2)}</p>
                              <p className="text-gray-400">{day.loads} loads</p>
                            </div>

                            {/* The Bar */}
                            <div 
                              className="w-full rounded-t-md transition-all duration-500 ease-out group-hover:opacity-80 relative"
                              style={{ 
                                height: `${Math.max(heightPercent, 2)}%`, // At least 2% so empty days still show a blip
                                background: day.spent > 0 ? "#1a2744" : "#f3f4f6"
                              }}
                            >
                              {/* Overlay loads count inside bar if it's tall enough */}
                              {heightPercent > 20 && day.loads > 0 && (
                                <span className="absolute top-2 left-1/2 -translate-x-1/2 text-[9px] font-bold text-white/50">
                                  {day.loads}
                                </span>
                              )}
                            </div>

                            {/* X-Axis Label */}
                            <span className="text-[9px] text-gray-400 font-medium text-center mt-3 truncate absolute -bottom-5 w-full">
                              {day.date}
                            </span>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              </>
            )}

          </div>
        </main>
        <BottomNav activeNav={activeNav} setActiveNav={setActiveNav} />
      </div>
    </div>
  );
}